import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, FileText } from 'lucide-react';
import Footer from '../components/Footer';

export default function TermsPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#161616] text-white flex flex-col">
      <nav className="fixed top-0 w-full z-50 backdrop-blur-md bg-[#161616]/80 border-b border-white/5">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center">
          <button onClick={() => navigate('/')} className="text-xl sm:text-2xl font-bold tracking-tight cursor-pointer" style={{ fontFamily: 'Clash Display' }} data-testid="terms-logo-btn">
            Move<span className="text-[#C6A97C]">More</span>
          </button>
          <button onClick={() => navigate('/')} className="inline-flex items-center gap-2 text-sm text-gray-300 hover:text-white transition" data-testid="terms-home-btn">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </button>
        </div>
      </nav>

      <section className="pt-32 pb-16 px-6 flex-1">
        <div className="container mx-auto max-w-3xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 rounded-full bg-[#D9C7A3] flex items-center justify-center">
                <FileText className="w-7 h-7 text-[#0B0B0B]" strokeWidth={2.5} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-[#C6A97C] mb-1">LEGAL</div>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight" style={{ fontFamily: 'Clash Display' }}>Terms of Service</h1>
              </div>
            </div>

            <div className="space-y-8 text-gray-300 leading-relaxed">
              <Section title="1. Acceptance of terms">
                By creating an account, purchasing a course, or subscribing to MoveMore (operated by Burke Biokinetics), you agree to be bound by these Terms. If you don't agree, please don't use the service.
              </Section>

              <Section title="2. The service">
                We provide on-demand video-based exercise rehabilitation programs and coaching content. The service is intended for general educational and rehabilitation purposes and does not replace personalised medical advice from your physician or healthcare provider.
              </Section>

              <Section title="3. Medical disclaimer">
                Always consult a qualified healthcare professional before starting any exercise programme — especially after injury or surgery. By using MoveMore, you confirm you've cleared the activity with your medical provider and accept that you exercise at your own risk. Burke Biokinetics is not liable for injuries arising from improper or unsupervised use of the programmes.
              </Section>

              <Section title="4. Accounts">
                You're responsible for keeping your login credentials secure. Don't share your account, and notify us immediately of any unauthorised access. We may suspend accounts that violate these Terms.
              </Section>

              <Section title="5. Payment & billing">
                All prices are in South African Rand (ZAR), processed via Yoco. Subscriptions renew automatically until cancelled. Cancel anytime from your dashboard or by emailing us — cancellation stops future billing but doesn't refund the current period (see our <a href="/refund-policy" className="text-[#C6A97C] hover:underline">Refund Policy</a>).
              </Section>

              <Section title="6. Content & intellectual property">
                All videos, programmes, written content, and platform code are the property of Burke Biokinetics. You receive a personal, non-transferable licence to access purchased content for your own use. You may not download, redistribute, resell, or publicly display the content without written permission.
              </Section>

              <Section title="7. Acceptable use">
                Don't attempt to bypass paywalls, scrape video content, harass other users in community areas, or upload anything illegal, harmful, or that infringes on someone else's rights. We may remove content and terminate accounts at our discretion.
              </Section>

              <Section title="8. Termination">
                You can close your account anytime. We may suspend or terminate accounts that breach these Terms, in which case any unused portion of paid time may be refunded at our discretion in line with the Refund Policy.
              </Section>

              <Section title="9. Limitation of liability">
                To the maximum extent permitted by law, Burke Biokinetics' total liability for any claim relating to the service is limited to the amount you paid in the 12 months preceding the claim. We're not liable for indirect, incidental, or consequential damages.
              </Section>

              <Section title="10. Changes to these terms">
                We may update these Terms occasionally. Continued use after changes are posted means you accept the updated Terms. Material changes will be notified via email or an in-app banner.
              </Section>

              <Section title="11. Governing law">
                These Terms are governed by the laws of the Republic of South Africa. Any disputes will be resolved in the courts of South Africa.
              </Section>

              <Section title="12. Contact">
                Questions about these Terms? Email <a href="mailto:brandon@burkebiokinetics.co.za" className="text-[#C6A97C] hover:underline">brandon@burkebiokinetics.co.za</a>.
              </Section>
            </div>

            <div className="mt-12 pt-8 border-t border-white/10 text-sm text-gray-500">
              <p>Burke Biokinetics &middot; 76 Reagent Road, Sea Point</p>
              <p className="mt-1">Last updated: April 2026</p>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="text-xl sm:text-2xl font-bold mb-3 text-white" style={{ fontFamily: 'Clash Display' }}>{title}</h2>
      <div className="text-gray-300">{children}</div>
    </div>
  );
}
